import { RefreshCw, Activity } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { whatsappAccountService, HealthInfo } from '@/services/whatsapp-account.service';
import { Card } from '@/components/Card';
import clsx from 'clsx';
import toast from 'react-hot-toast';

const ratingMap: Record<string, { label: string; chipCls: string; dot: string }> = {
  GREEN:  { label: 'Healthy',  chipCls: 'bg-success-soft text-success border-success/20',  dot: 'bg-success'  },
  YELLOW: { label: 'Warning',  chipCls: 'bg-warning-soft text-warning border-warning/30',  dot: 'bg-warning'  },
  RED:    { label: 'At risk',  chipCls: 'bg-danger-soft text-danger border-danger/20',     dot: 'bg-danger'   },
};

interface Props { initial?: HealthInfo | null; accountUid?: string }

export const HealthCard: React.FC<Props> = ({ initial, accountUid }) => {
  const [health, setHealth] = useState<HealthInfo | null>(initial ?? null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setHealth(initial ?? null); }, [accountUid, initial?.fetchedAt, initial?.error, initial?.qualityRating, initial?.messagingLimitTier]);

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await whatsappAccountService.health(true, accountUid);
      setHealth(r);
      if (r.error) toast.error(r.error);
      else toast.success('Health refreshed');
    } catch { /* toast shown */ } finally { setBusy(false); }
  };

  const rating = health?.qualityRating;
  const m = rating ? (ratingMap[rating] ?? { label: rating, chipCls: 'bg-surface-alt text-text-muted border-border', dot: 'bg-text-muted' }) : null;

  return (
    <Card padded>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-soft text-primary">
            <Activity size={17} />
          </div>
          <div>
            <div className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">WhatsApp Health</div>
            <div className="mt-0.5 font-display text-xl font-bold tracking-tight text-text-primary tabular-nums">
              {health?.qualityRating ?? '—'}
            </div>
          </div>
        </div>

        <button
          onClick={refresh}
          disabled={busy}
          className="flex h-7 w-7 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary disabled:opacity-50 transition-colors"
          aria-label="Refresh health"
        >
          <RefreshCw size={14} className={busy ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {m && (
          <span className={clsx(
            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold',
            m.chipCls,
          )}>
            <span className={clsx('h-1.5 w-1.5 rounded-full', m.dot)} />
            {m.label}
          </span>
        )}
        {health?.messagingLimitTier && (
          <span className="inline-flex items-center rounded-full border border-border bg-surface-alt px-2.5 py-1 text-xs text-text-muted">
            Tier: {health.messagingLimitTier}
          </span>
        )}
      </div>

      {health?.staleSince && (
        <div className="mt-2 text-[10px] text-warning">
          {new Date(health.staleSince).getTime() === 0 ? 'Account health has not been verified yet.' : `Stale · last fetched ${new Date(health.staleSince).toLocaleString()}`}
          {health.error && <p className="mt-1">{health.error}</p>}
        </div>
      )}
      {health?.fetchedAt && !health.staleSince && (
        <div className="mt-2 text-[10px] text-text-muted">
          Updated {new Date(health.fetchedAt).toLocaleString()}
        </div>
      )}
    </Card>
  );
};
